import * as THREE from 'three/webgpu';
import { DT, resolveHftBeltSurface } from '@chaos/sim';
import { buildChartVisual } from './chart-visual.js';

const ROUTE_COLORS = [0x39e5ef, 0xc798ff, 0xffd166];
const HAZARD_COLOR = 0xff4b38;
const ROUTE_THREE_COLORS = ROUTE_COLORS.map((color) => new THREE.Color(color));
const WHITE = new THREE.Color(0xffffff);

function routeFor(partId, platforms) {
  const baseId = partId.includes('_arrow_') ? partId.split('_arrow_')[0] : partId;
  const platform = platforms.get(baseId);
  return platform?.surface?.kind === 'conveyor' ? { platform, route: platform.surface.hftRouteGroup } : null;
}

/** Runtime-only treatment for the approved HFT export; mechanics remain sim-owned. */
export function buildHftVisual(root, map, asset, { upcoming = () => [] } = {}) {
  buildChartVisual(root, map, asset);
  const platforms = new Map(map.platforms.map((platform) => [platform.partId, platform]));
  const owned = [...root.children];
  const arrows = [];
  const belts = [];
  let disposed = false;

  for (const mesh of owned) {
    const id = mesh.userData.partId ?? '';
    const tagged = routeFor(id, platforms);
    if (tagged && Number.isInteger(tagged.route)) {
      mesh.userData.hftRouteGroup = tagged.route;
      if (id.includes('_arrow_')) {
        mesh.material.color.setHex(ROUTE_COLORS[tagged.route]);
        if (mesh.material.emissive) mesh.material.emissive.setHex(ROUTE_COLORS[tagged.route]);
        mesh.userData.hftRole = 'arrow';
        arrows.push({ mesh, platform: tagged.platform, anchor: mesh.position.clone(), rotationY: mesh.rotation.y,
          baseColor: mesh.material.color.clone() });
      } else {
        mesh.material.color.setHex(ROUTE_COLORS[tagged.route]).multiplyScalar(.28);
        if (mesh.material.emissive) mesh.material.emissive.copy(mesh.material.color);
        mesh.userData.hftRole = 'belt';
        belts.push({ mesh, route: tagged.route, baseColor: mesh.material.color.clone() });
      }
    } else if (/^hft_hot_/.test(id)) {
      mesh.userData.hftRole = 'hazard';
      mesh.material.color.setHex(HAZARD_COLOR);
      if (mesh.material.emissive) mesh.material.emissive.setHex(HAZARD_COLOR);
    }
  }

  function update(world) {
    if (disposed || !world) return;
    const warningRoutes = new Set(upcoming()
      .filter((event) => event.mutation?.kind === 'hft_belt_flow' && event.announceTick <= world.tick && world.tick < event.applyTick)
      .map((event) => event.mutation.routeGroup));
    const pulse = .65 + .35 * Math.sin(world.tick * .35);
    for (const belt of belts) belt.mesh.material.color.copy(belt.baseColor)
      .lerp(ROUTE_THREE_COLORS[belt.route], warningRoutes.has(belt.route) ? .18 + pulse * .18 : 0);

    for (const arrow of arrows) {
      const surface = resolveHftBeltSurface(world, arrow.platform.surface);
      const reversed = surface.dirX * arrow.platform.surface.dirX + surface.dirZ * arrow.platform.surface.dirZ < 0;
      arrow.mesh.rotation.y = arrow.rotationY + (reversed ? Math.PI : 0);
      // Compress travel while retaining each route's relative carry speed.
      const travel = ((world.tick * surface.speed * DT * DT * .2) % .5) - .25;
      arrow.mesh.position.copy(arrow.anchor);
      arrow.mesh.position.x += surface.dirX * travel;
      arrow.mesh.position.z += surface.dirZ * travel;
      arrow.mesh.material.color.copy(arrow.baseColor)
        .lerp(WHITE, warningRoutes.has(surface.hftRouteGroup) ? .16 + pulse * .24 : 0);
    }
  }

  function dispose() {
    if (disposed) return;
    disposed = true;
    for (const mesh of owned) {
      root.remove(mesh);
      mesh.geometry?.dispose();
      if (mesh.userData.ownsMaterial) mesh.material?.dispose();
    }
  }

  return { update, dispose };
}
